import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../../../../auth/AuthContext";
import { useToast } from "../../../../../hooks/use-toast";
import { creditoAdhocService } from "../services/credito-adhoc-service";
import { CreateCreditoAdhocInput } from "../types";

export const creditoAdhocKeys = {
  all: ["credito-adhoc"] as const,
  list: (dbName: string, ops?: string) =>
    [...creditoAdhocKeys.all, "list", dbName, ops] as const,
  franchises: (dbName: string) =>
    [...creditoAdhocKeys.all, "franchises", dbName] as const,
};

export function useCreditoAdhocList(dbName: string, ops?: string) {
  return useQuery({
    queryKey: creditoAdhocKeys.list(dbName, ops),
    queryFn: async () => {
      if (!dbName) return [];
      const response = await creditoAdhocService.list(dbName, ops);
      return response.data?.data || [];
    },
    enabled: !!dbName,
    staleTime: 1000 * 30,
  });
}

export function useFranquiasAdHoc(dbName: string) {
  return useQuery({
    queryKey: creditoAdhocKeys.franchises(dbName),
    queryFn: async () => {
      if (!dbName) return [];
      const response = await creditoAdhocService.listFranchises(dbName);
      return response.data?.data || [];
    },
    enabled: !!dbName,
    staleTime: 1000 * 60 * 5,
  });
}

export function useCreateCreditoAdhoc() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (
      params: Omit<CreateCreditoAdhocInput, "userInfo">
    ) => {
      if (!user?.email) throw new Error("Usuário não autenticado.");
      return creditoAdhocService.create({
        ...params,
        userInfo: { email: user.email },
      });
    },
    onSuccess: (response, variables) => {
      const data = response.data;
      toast({
        title: "Crédito AdHoc criado",
        description: `Sol ID: ${data?.solId}. Processamento CNAB iniciado.`,
      });
      queryClient.invalidateQueries({
        queryKey: creditoAdhocKeys.list(variables.dbName),
      });
    },
    onError: (error: any) => {
      toast({
        title: "Erro ao criar crédito",
        description: error?.response?.data?.message || "Erro inesperado.",
        variant: "destructive",
      });
    },
  });
}

export function useTriggerBaixa() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      dbName,
      creditoAdhocId,
    }: {
      dbName: string;
      creditoAdhocId: number;
    }) => {
      if (!user?.email) throw new Error("Usuário não autenticado.");
      return creditoAdhocService.triggerBaixa(dbName, creditoAdhocId, {
        email: user.email,
      });
    },
    onSuccess: (_, variables) => {
      toast({
        title: "Baixa iniciada",
        description: "Geração do CNAB de baixa foi disparada com sucesso.",
      });
      queryClient.invalidateQueries({
        queryKey: creditoAdhocKeys.list(variables.dbName),
      });
    },
    onError: (error: any) => {
      toast({
        title: "Erro ao iniciar baixa",
        description: error?.response?.data?.message || "Erro inesperado.",
        variant: "destructive",
      });
    },
  });
}
